import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { once } from 'lodash';
import { CancellationTokenSource, NotificationHandler } from 'vscode-languageserver';
import { Disposable } from '@gitlab-org/disposable';
import { CodeSuggestionRequest, GitLabApiClient, GenerationType } from '../api';
import { AdditionalContext } from '../api_types';
import { CircuitBreaker } from '../circuit_breaker/circuit_breaker';
import { IDocContext } from '../document_transformer_service';
import { isFetchError } from '../fetch_error';
import { log } from '../log';
import { TRACKING_EVENTS } from '../tracking/constants';
import { SuggestionSource, TelemetryTracker } from '../tracking/tracking_types';
import { PostProcessorPipeline } from '../suggestion_client/post_processors/post_processor_pipeline';
import { Notifier, NotifyFn } from '../notifier';
import { StreamingCompletionResponse, StreamWithId } from '../notifications';
import { SuggestionApiErrorCheck } from '../feature_state/suggestion_api_error_check';

export interface StartStreamParams {
  streamId: string;
  uniqueTrackingId: string;
  documentContext: IDocContext;
  userInstruction?: string;
  generationType?: GenerationType;
  additionalContexts?: AdditionalContext[];
}

export interface StreamingHandler extends Notifier<StreamingCompletionResponse> {
  notificationHandler: NotificationHandler<StreamWithId>;
  startStream({
    additionalContexts,
    documentContext,
    streamId,
    uniqueTrackingId,
    userInstruction,
    generationType,
  }: StartStreamParams): Promise<void>;
}

export const StreamingHandler = createInterfaceId<StreamingHandler>('StreamingHandler');

@Injectable(StreamingHandler, [
  GitLabApiClient,
  PostProcessorPipeline,
  TelemetryTracker,
  SuggestionApiErrorCheck,
])
export class DefaultStreamingHandler implements StreamingHandler {
  #api: GitLabApiClient;

  #postProcessorPipeline: PostProcessorPipeline;

  #tracker: TelemetryTracker;

  #streamCancellations: Map<string, () => void> = new Map();

  #streamingCompletionNotifyFn: NotifyFn<StreamingCompletionResponse> | undefined;

  #circuitBreaker: CircuitBreaker;

  constructor(
    api: GitLabApiClient,
    postProcessorPipeline: PostProcessorPipeline,
    tracker: TelemetryTracker,
    apiErrorCheck: SuggestionApiErrorCheck,
  ) {
    this.#api = api;
    this.#postProcessorPipeline = postProcessorPipeline;
    this.#tracker = tracker;
    this.#circuitBreaker = apiErrorCheck;
  }

  init(streamingCompletionNotifyFn: NotifyFn<StreamingCompletionResponse>) {
    this.#streamingCompletionNotifyFn = streamingCompletionNotifyFn;
  }

  notificationHandler(stream: StreamWithId): void {
    const cancellationHandler = this.#streamCancellations.get(stream.id);
    if (!cancellationHandler) {
      log.warn(
        `StreamingHandler received notification to close stream id ${stream.id} but the cancellation handler is missing.`,
      );
      return;
    }
    cancellationHandler();
  }

  #onStreamCancellation(streamId: string, listener: () => void): Disposable {
    this.#streamCancellations.set(streamId, listener);
    return {
      dispose: () => {
        this.#streamCancellations.delete(streamId);
      },
    };
  }

  #sendStreamingResponse(response: StreamingCompletionResponse) {
    if (!this.#streamingCompletionNotifyFn) {
      throw new Error(
        'The StreamingHandler has not been initialized. This is non-recoverable error and needs a bug-fix. Please create an issue.',
      );
    }
    return this.#streamingCompletionNotifyFn(response);
  }

  async startStream({
    additionalContexts,
    documentContext,
    streamId,
    uniqueTrackingId,
    userInstruction,
    generationType,
  }: StartStreamParams) {
    this.#tracker.setCodeSuggestionsContext(uniqueTrackingId, {
      documentContext,
      additionalContexts,
      source: SuggestionSource.network,
      isStreaming: true,
    });

    let streamShown = false;
    let suggestionProvided = false;
    let streamCancelledOrErrored = false;

    const cancellationTokenSource = new CancellationTokenSource();
    const disposeStopStreaming = this.#onStreamCancellation(streamId, () => {
      streamCancelledOrErrored = true;
      cancellationTokenSource.cancel();

      if (streamShown) {
        this.#tracker.updateSuggestionState(uniqueTrackingId, TRACKING_EVENTS.REJECTED);
      } else {
        this.#tracker.updateSuggestionState(uniqueTrackingId, TRACKING_EVENTS.CANCELLED);
      }
    });
    const cancellationToken = cancellationTokenSource.token;

    const endStream = async () => {
      await this.#sendStreamingResponse({
        id: streamId,
        done: true,
      });

      if (!streamCancelledOrErrored) {
        this.#tracker.updateSuggestionState(uniqueTrackingId, TRACKING_EVENTS.STREAM_COMPLETED);

        if (!suggestionProvided) {
          this.#tracker.updateSuggestionState(uniqueTrackingId, TRACKING_EVENTS.NOT_PROVIDED);
        }
      }
    };

    this.#circuitBreaker.success();

    const request: CodeSuggestionRequest = {
      prompt_version: 1,
      project_path: '',
      project_id: -1,
      current_file: {
        content_above_cursor: documentContext.prefix,
        content_below_cursor: documentContext.suffix,
        file_name: documentContext.fileRelativePath,
      },
      intent: 'generation',
      stream: true,
      ...(additionalContexts?.length && {
        context: additionalContexts,
      }),
      ...(userInstruction && {
        user_instruction: userInstruction,
      }),
      generation_type: generationType,
    };

    const trackStreamStarted = once(() => {
      this.#tracker.updateSuggestionState(uniqueTrackingId, TRACKING_EVENTS.STREAM_STARTED);
    });

    const trackStreamShown = once(() => {
      this.#tracker.updateSuggestionState(uniqueTrackingId, TRACKING_EVENTS.SHOWN);
      streamShown = true;
    });

    try {
      for await (const response of this.#api.getStreamingCodeSuggestions(request)) {
        if (cancellationToken.isCancellationRequested) {
          break;
        }

        if (this.#circuitBreaker.isOpen()) {
          break;
        }

        trackStreamStarted();

        const processedCompletion = await this.#postProcessorPipeline.run({
          documentContext,
          input: { id: streamId, completion: response, done: false },
        });
        await this.#sendStreamingResponse(processedCompletion);

        if (response.replace(/\s/g, '').length) {
          trackStreamShown();
          suggestionProvided = true;
        }
      }
    } catch (err) {
      this.#circuitBreaker.error();

      if (isFetchError(err)) {
        this.#tracker.updateCodeSuggestionsContext(uniqueTrackingId, { status: err.status });
      }
      this.#tracker.updateSuggestionState(uniqueTrackingId, TRACKING_EVENTS.ERRORED);
      streamCancelledOrErrored = true;

      log.error('Error streaming code suggestions.', err);
    } finally {
      await endStream();
      disposeStopStreaming.dispose();
      cancellationTokenSource.dispose();
    }
  }
}
